import {browserHistory} from 'react-router';
import {Map, List, Iterable} from 'immutable';
import immutableStore from 'alt-utils/lib/ImmutableUtil';
import alt from 'components/Dispatcher';
import LibraryActions from 'actions/library/LibraryActions';
import ConstValues, {ALL_OF_VALUE, KEYWORD_TOPIC} from 'utils/const';
import AuthStore from 'stores/AuthStore';
import FileUploadCustomContentSource from 'sources/files/FileUploadCustomContentSource';
import Utils from './Utils';
import {Field, SearchResults} from 'records/Search';
import SearchSource from 'sources/SearchSource';
import LibrarySource from 'sources/library/LibrarySource';
import {Data, Int} from '@store/@types/util';
import {SearchTypes} from 'utils/const';
import {Store} from '@store/@types/alt';
import SearchTabActions from 'actions/search/SearchTabActions';
import SavedSearchActions from 'actions/search/SavedSearchActions';
import CrmSyncActions from 'actions/crm/CrmSyncActions';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import CallActions from 'actions/CallActions';
import SearchInputActions from 'actions/search/SearchInputActions';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import FileUploadCallDetailsActions from 'actions/files/FileUploadCallDetailsActions';
import {searchFieldNameFromMenuItemPrimaryText} from 'stores/searchFieldNameFromMenuItem';
import FileUploadActions from 'actions/files/FileUploadActions';
import CallerTeamComboboxSearchFieldActions from 'actions/search/CallerTeamComboboxSearchFieldActions';
import VotingRadioButtonsSearchFieldActions from 'actions/search/VotingRadioButtonsSearchFieldActions';
import CallerTeamComboboxSearchFieldStore from './search/CallerTeamComboboxSearchFieldStore';
import VotingRadioButtonsSearchFieldStore from './search/voting/VotingRadioButtonsSearchFieldStore';
import CallCardActions from '@store/call-card/CallCardActions';

const defaultLimit = ConstValues.numberOfCallsPerPage;
const defaultPage = null;
const st = ConstValues.searchTypes;

interface SearchStoreStateInterface {
  // `true` if the user is performing an advanced search, i.e., if the search sidebar should be opened or not
  isAdvancedSearch: boolean;
  // `true` if the user is searching library tags / on the "Library" tab page
  isSearchingLibraryTags: boolean;
  // `true` if the user is searching for his / her own calls, i.e., the user is on the "My Calls" tab page
  isSearchingSelf: boolean;
  // `queuedSearch` stores the last search requested by the user while there is a current search that's in progress
  queuedSearch: Map<unknown, unknown>;
  // If 'fileId' is set, the search should returns only the matching FileUpload
  fileId: string;
  // `true` if server has no elasticsearch index for the current user's account
  missingIndex: boolean;
  isInitialLoad: boolean;
  searchLoading: boolean;
  searchError: boolean;
  searchResults: SearchResults;
  searchFields: Map<string, unknown>;
  searchLimit: Int;
  searchPage: Int | null;
  // `searchTotal` is the number of total results returned from the current search request
  searchTotal: Int;
  // `prevSearchTypes` is a list of search type(s) chosen previously by the user. It's a list because the user
  // could accumulate / choose multiple search type(s) without waiting for the previous searches to complete
  prevSearchTypes: List<unknown>;
  // `searchType` is the type of text-based search that's provided by the string of the text input box at the top
  // of the page. TODO remove | string
  searchType: SearchTypes | string;
  // `userIsSearchingSth` is set to true whenever user is searching some calls either through the search input box
  // or the search sidebar
  userIsSearchingSth: boolean;
  searchTerm: string;
  sortByDate: any; // TODO
}

class SearchStoreState extends Data<SearchStoreStateInterface>({
  isAdvancedSearch: false,
  isSearchingLibraryTags: false,
  isSearchingSelf: false,
  queuedSearch: Map(),
  fileId: '',
  missingIndex: false,
  isInitialLoad: true,
  searchLoading: false,
  searchError: false,
  searchResults: new SearchResults(),
  searchFields: Map(),
  searchLimit: defaultLimit,
  searchPage: defaultPage,
  searchTotal: 0,
  prevSearchTypes: List(),
  searchType: st.miAll,
  userIsSearchingSth: false,
  searchTerm: '',
  sortByDate: true,
}) {}

/**
 * SearchStore. Handles Search actions
 * Notice immutableStore decorator. It doesn't do much other than adding
 * hooks on serialization and deserialization
 */
@immutableStore
class SearchStore_unconnected extends Store<SearchStoreState> {
  utils = new Utils();

  constructor() {
    super();
    this.state = new SearchStoreState();
    this.bindActions(CallActions);
    this.bindActions(CrmSyncActions);
    this.bindActions(LibraryActions);
    this.bindActions(SearchTabActions);
    this.bindActions(SearchInputActions);
    this.bindActions(ManagerDashboardActions);
    this.registerAsync(FileUploadCustomContentSource);
    this.registerAsync(LibrarySource);
    this.registerAsync(SearchSource);
  }

  onUpdateIsInitialLoad(isInitialLoad) {
    const state = this.state.set('isInitialLoad', isInitialLoad);
    this.setState(state);
  }

  /**
   * Update the two-way-sync results data into `this.state` by sending a request to the backend to retrieve the
   * custom-content related data
   * @param args (Array<String>) [fileUploadId, contact, company, crmLink]
   */
  onUpdateCallSyncData(args) {
    const id = args[0];
    const timezone = args[args.length - 1];
    setImmediate(() => {
      this.getInstance<typeof FileUploadCustomContentSource>().fetchCustomContent(id, timezone);
    });
  }

  /**
   * Update the corresponding document within this.state.get('searchResults') so the changes in the custom-content and
   * related fields such as contact and company are immediately reflected in the front-end
   * @param resp {Object} server-side response for a two-way-sync saving request
   */
  onFetchCustomContentSuccess(resp) {
    const searchResults = this.utils.searchResultsWithCustomContentData(
      this.state.searchResults,
      resp.data
    );
    this.setState(this.state.merge({searchResults}));
    FileUploadCallDetailsActions.hideCallSyncCRM.defer();
    CallCardActions.getCall.defer(resp.data.id);
  }

  /** SearchTabActions related handlers */
  onSwitchWithMyCalls() {
    this.onUpdateFileId('');
    this.clearCallerFromFieldsAndStates();
  }

  clearCallerFromFieldsAndStates() {
    const fields = this.fieldsWithoutCaller(this.state.searchFields);
    this.setState(this.state.set('searchFields', fields));
  }

  /** Event handler for `CallActions.clearPrevSearchTypes` */
  onClearPrevSearchTypes() {
    const state = this.state.set('prevSearchTypes', List());
    this.setState(state);
  }

  /**
   * Remove the search field specified by `searchType` from `fields` so that it doesn't interfere with a future
   * search request
   * @param fields {object} Current search fields object
   * @param searchType {string} Current search type
   */
  removeSearchTypeFields(fields, searchType) {
    if (fields) {
      const removeField = Map.isMap(fields)
        ? name => fields.remove(name)
        : name => {
            delete fields[name];
            return fields;
          };
      switch (searchType) {
        case st.miCaller:
          return removeField('Caller');
        case st.miContact:
          return removeField('Contact');
        case st.miCompany:
          return removeField('Company');
        case st.miCallStage:
          return removeField('Call Stage');
        case st.miCampaign:
          return removeField('Campaign');
        case st.miKeywords:
          return removeField('Keyword / Topic');
        case st.miAnnotations:
          const get = Map.isMap(fields) ? name => fields.get(name) : name => fields[name];
          const set = Map.isMap(fields)
            ? (name, value) => fields.set(name, value)
            : (name, value) => {
                fields[name] = value;
                return fields;
              };
          const orList = List(get('or')).filter(value => {
            const keys = List(Map.isMap(value) ? value.keySeq() : Object.keys(value as any));
            return !(keys.includes('Comments') || keys.includes('Highlights'));
          });
          return orList.count() > 0 ? set('or', orList.toArray()) : removeField('or');
        default:
          break;
      }
    }
    return fields;
  }

  /** Return this.state without searchType-related fields and fieldsState */
  stateWithoutSearchType(state, searchType) {
    const fields = this.removeSearchTypeFields(state.get('searchFields'), searchType);
    state = state.set('searchFields', fields);
    return state;
  }

  /**
   * Since the search term has been cleared and the search-type has been reset, reset
   * this.state.get('shouldClearSearchTerm') back to false
   */
  onClearShouldClearSearchTerm() {
    this.setState({searchType: ConstValues.searchTypes.miAll});
  }

  /** Update this.state.get('searchType') and perform a new search */
  onUpdateSearchType([prevSearchType, searchType]) {
    let state = this.state.set('searchType', searchType);
    let prevSearchTypes = this.state.prevSearchTypes;
    prevSearchTypes = prevSearchTypes.push(prevSearchType);
    state = state.set('prevSearchTypes', prevSearchTypes);
    state = this.stateWithoutSearchType(state, prevSearchType);
    this.setState(state);
    this.performSearchUsingState();
  }

  /** Reset this.state.get('searchType') and perform a fresh search after the current search term is cleared */
  onResetSearchType() {
    const searchType = this.state.searchType;
    let state = this.state;
    state = this.stateWithoutSearchType(state, searchType);
    this.setState(state);
    this.performSearchUsingState();
  }

  /**
   * restore a previously saved search state
   * @param newState
   */
  onResetSearch(newState) {
    this.setState(newState.searchStoreState);
  }

  /**
   * Return an object whose keys are the parameters for the current active search so that the
   * caller of this function can deconstruct the returned object into multiple local constants to
   * be used as the search parameters
   * @return {object} An object whose keys are the parameters for the current active search
   */
  currentSearchParameters() {
    return {
      term: this.state.searchTerm,
      fields: this.state.searchFields,
      limit: this.state.searchLimit,
      page: this.state.searchPage,
    };
  }

  /**
   * Return a `fields` object whose `Library` and `Is In Library` keys are removed
   * @param {object} fields Dictionary of fields used during a search
   * @return {object} Dictionary of fields whose `Library` and `Is In Library` keys have been removed
   */
  fieldsWithoutLibrary(fields) {
    delete fields['Library'];
    delete fields['Is In Library'];
    return fields;
  }

  /**
   * Return a `fields` object whose `Caller` key is removed
   * @param {object} fields Dictionary of fields used during a search
   * @return {object} Dictionary of fields whose `Caller` key has been removed
   */
  fieldsWithoutSelf(fields) {
    delete fields['Caller'];
    return fields;
  }

  /**
   * Return a `fields` object whose `Id` key is removed
   * @param {object} fields Dictionary of fields used during a search
   * @return {object} Dictionary of fields whose `Id` key has been removed
   */
  fieldsWithoutFileId(fields) {
    delete fields['Id'];
    return fields;
  }

  /**
   * Return a `fields` object whose `Caller` key has been replaced by AuthStore.getState().get('user')
   * @param  {object} fields A dictionary of fields used during a search
   * @return {object}        A dictionary of fields whose `Caller` key has been replaced by
   *                           AuthStore.getState().get('user')
   */
  fieldsReplacedWithSelf(fields) {
    this.waitFor(AuthStore);
    delete fields['Caller'];
    const user = AuthStore.getState().user;
    if (Map.isMap(fields)) {
      fields = fields.toObject();
    }
    fields['user_id'] = user.id;
    return fields;
  }

  /**
   * Return a `fields` object whose `Caller` key has been removed
   * @param {object} fields Dictionary of fields used during a search
   * @return {object} Dictionary of fields
   */
  fieldsWithoutCaller(fields) {
    return fields.delete('Caller').delete('user_id');
  }

  /**
   * Return a `fields` object whose `Id` key has been replaced by this.state.get('fileId')
   * @param  {object} fields A dictionary of fields used during a search
   * @return {object}        A dictionary of fields whose `Id` key has been replaced by
   *                           this.state.get('fileId')
   */
  fieldsReplacedWithFileId(fields) {
    if (Map.isMap(fields)) {
      fields = fields.toObject();
    }
    fields.Id = this.state.fileId;
    return fields;
  }

  /** Return the `fields` dictionary used during any search */
  fieldsToSearch() {
    const fields = this.state.searchFields;
    return this.augmentFields(fields);
  }

  /**
   * Return the `fields` dictionary augmented with relevant state values
   */
  augmentFields(fields) {
    return this.replaceFields(fields);
  }

  /**
   * Return a fields object whose content has been replaced with relevant state values
   * @param  {object} fields A search request's `fields` object
   * @return {object}        A search request's `fields` object whose content has been replaced
   */
  replaceFields(fields) {
    if (Map.isMap(fields)) {
      fields = fields.toObject();
    }
    return this.fieldsReplacedWithState(this.state, fields);
  }

  fieldsReplacedWithState(state, fields) {
    if (state.get('isSearchingSelf')) {
      fields = this.fieldsReplacedWithSelf(fields);
    }
    if (state.get('fileId')) {
      fields = this.fieldsReplacedWithFileId(fields);
    }
    return fields;
  }

  /** Return a fields object whose keys are modified according to the current search term and
   * searchType only if it's necessary
   * @param fields {object} Current fields object
   * @param searchTerm {string} Current search term
   */
  fieldsAugmentedWithSearchType(fields, searchTerm) {
    const searchType = this.state.searchType;
    if (searchType !== st.miAll) {
      return fieldsAugmentedWithSearchTypeAndTerm(fields, searchType, searchTerm);
    }
    return fields;
  }

  /**
   * Action handler that's triggered when the user wants to search calls based on their `library_tags`
   */
  onLibraryTagsSearch() {
    this.performSearchUsingState();
  }

  /**
   * Action handler that's triggered
   * @return {[type]} [description]
   */
  onSelfSearch() {
    this.performSearchUsingState();
  }

  /**
   * Perform a fields search using `this.state`. Notice that we re-use all the relevant properties within `this.state`
   * as-is since this method / function should not be the one to care about which tab page the user is on
   */
  performSearchUsingState() {
    const term = this.state.searchTerm;
    const limit = this.state.searchLimit;
    const page = this.state.searchPage;
    let fields = this.fieldsToSearch();
    if (term.length > 0) {
      fields = this.fieldsAugmentedWithSearchType(fields, term);
    }
    this.performOrQueueSearch(term, fields, limit, page);
  }

  /**
   * Action handler that's triggered when the user wants to change the current page on calls/my
   * @param  {number} page Number of the page to change
   */
  onChangePage(page) {
    const term = this.state.searchTerm;
    let fields = this.fieldsToSearch();
    const limit = this.state.searchLimit;
    if (term.length > 0) {
      fields = this.fieldsAugmentedWithSearchType(fields, term);
    }
    this.performOrQueueSearch(term, fields, limit, page);
  }

  /** Return `true` if the user is searching "All Calls" */
  isSearchingAllCalls(state) {
    return !(state.get('isSearchingLibraryTags') || state.get('isSearchingSelf'));
  }

  /**
   * The user clicked the "Clear Search" flat button. Notice that we perform different kinds of clear depending on whether
   * the user is on the "All Calls", "Library" or "My Calls" tab page
   */
  onClearFieldsSearch() {
    const state = this.state;
    const term = this.state.searchTerm;
    this.clearFields(state, term);
    this.performSearchUsingState();
  }

  /** Handler to clear fields search for all pages
   * @param {string} term Current term specified in the search input box
   */
  clearFields(state, term) {
    const fields = {}; // TODO i have no idea what this is, what its doing, if this is obsolete this.replaceFields({});
    this.setState(
      state.merge({
        searchTerm: term,
        searchFields: fields,
        searchLimit: defaultLimit,
        searchPage: defaultPage,
        searchType: st.miAll,
        fileId: '',
      })
    );
    this.resetFields(state, fields);
  }

  resetFields(state, fields) {
    if (this.isSearchingAllCalls(state)) {
      SearchFieldsActions.clearAllSearchFieldStates.defer();
    } else {
      SearchFieldsActions.clearMyOrLibraryTagSearchFieldStates.defer(fields);
    }
  }

  onExecuteInactiveSearch(savedSearch) {
    if (this.state.searchLoading) {
      setTimeout(() => {
        CallActions.executeInactiveSearch(savedSearch);
      }, 1000);
    } else {
      const {data} = savedSearch;
      // Show the user any saved search term.
      this.onUpdateSearchTerm(data.get('term'));

      this.performFieldsSearch(
        data.get('term'),
        data.get('fields'),
        data.get('limit'),
        data.get('page')
      );
      const link = convertFullyQualifiedURLToRelativeURL(savedSearch.permalink);
      browserHistory.push(link);
      setTimeout(() => {
        CallActions.setIsAdvancedSearch(true);
      }, 1000);
    }
  }

  /**
   * CallActions.fieldsSearch handler
   * @param fields {Object / Map} Current search fields
   * @param _ {string} Name of the action
   * @param limit {number} Limit of the search results
   * @param page {number} Page number of the search results
   */
  onFieldsSearch(fields, _, limit = defaultLimit, page = defaultPage) {
    const term = this.state.searchTerm;
    let reqFields = this.augmentFields(fields);
    reqFields = Map(reqFields);
    this.performFieldsSearch(term, reqFields, limit, page);
  }

  /**
   * CallActions.addQueueSearch handler
   * @param args  Array [term, fields]
   *
   * This is intended to be used instead of CallAction.xxxSearch.defer
   */
  onAddQueueSearch(args) {
    const term = args[0];
    const fields = args[1];
    let reqFields = this.augmentFields(fields);
    reqFields = Map(reqFields);
    this.queueSearch(term, reqFields, defaultLimit, defaultPage);
  }

  /**
   * Handle CallActions.termFieldsSearch which happens when the user types in a search term or when the user refreshes
   * the search page
   * @param args Array [term, fields]
   */
  onTermFieldsSearch([term, fields, limit = defaultLimit, page = defaultPage]) {
    if (Map.isMap(fields)) {
      fields = fields.toObject();
    }
    fields = this.augmentFields(fields);
    fields = this.fieldsAugmentedWithSearchType(fields, term);
    this.performFieldsSearch(term, fields, limit, page);
  }

  /**
   * Perform a fields-based search using required search metadata such as term, fields, limit and page.
   * @param  {string} term           A string search term
   * @param  {object} fields         A object of search fields restrctions
   * @param  {number} limit          An integer limit
   * @param  {number} page           An integer page number
   */
  performFieldsSearch(term, fields, limit, page) {
    if (term.length > 0) {
      fields = this.fieldsAugmentedWithSearchType(fields, term);
    }
    this.performOrQueueSearch(term, fields, limit, page);
  }

  /** Handle the event when CallActions.clearSearch is called */
  onClearSearch() {
    let state = this.state.set('isAdvancedSearch', false);
    state = state.set('searchResults', new SearchResults());
    this.clearFields(state, '');
    this.performSearchUsingState();
  }

  onClearSearchInputValue() {
    this.setState(this.state.set('searchTerm', ''));
  }

  onUpdateSearchTerm(searchTerm) {
    this.setState(this.state.set('searchTerm', searchTerm));
    this.onUpdateFileId('');
  }

  /**
   * Update this.state.get('isAdvancedSearch') which dictates
   * @param  {Boolean} isAdvancedSearch [description]
   * @return {[type]}                   [description]
   */
  onSetIsAdvancedSearch(isAdvancedSearch) {
    const state = this.state.set('isAdvancedSearch', isAdvancedSearch);
    this.setState(state);
  }

  /**
   * Update this.state.get('isSearchingSelf') which dictates whether this.state.get('searchFields').Caller should
   * be replaced by AuthStore.state.get('user') or not
   * @param  {Boolean} isSearching `true` or `false`
   */
  onSetIsSearchingSelf(isSearching) {
    const state = this.state.set('isSearchingSelf', isSearching);
    this.setState(state);
    const searchTerm = this.state.searchTerm;
    if (!isSearching) {
      this.removeCallerFromSearchFields();
      if (searchTerm && searchTerm.length === 0) {
        this.performStarSearch();
      }
    }
  }

  /**
   * Update this.state.get('fileId') which, if set, and a string returns the page containing the
   * fileupload with that id when performing a search
   * if array then returns a page with multiple file ids specified
   * @param  {String, array} fileId one or more file ids
   * XXX EV-10337 change this so that fileId is fileIds a list
   */
  onUpdateFileId(fileId) {
    const state = this.state.merge({
      fileId,
      searchPage: null,
    });
    this.setState(state);
  }

  /** Remove `Library` from this.state.get('searchFields') so that "Library" tab won't interfere with other normal searches */
  removeLibraryFromSearchFields() {
    this.removeFieldFromFields('Library');
  }

  /** Remove `Is In Library` from this.state.get('searchFields') so that "Is In Library" tab won't interfere with other normal searches */
  removeHasLibraryTagsFromSearchFields() {
    this.removeFieldFromFields('Is In Library');
  }

  /** Remove `Caller` from this.state.get('searchFields') so that "My Calls" tab won't interfere with other normal searches */
  removeCallerFromSearchFields() {
    this.removeFieldFromFields('Caller');
  }

  /** Remove a field from this.state.get('searchFields')
   * @param  {string} field Name of the field to remove
   */
  removeFieldFromFields(field) {
    const fields = this.state.searchFields;
    if (fields) {
      if (fields.hasOwnProperty(field)) {
        delete fields[field];
      }
      const state = this.state.set('searchFields', fields);
      this.setState(state);
    }
  }

  performStarSearch() {
    // Clear the search sidebar when the user switches back from "My Calls" to "All Calls"
    setImmediate(() => {
      const searchTerm = '*';
      SearchInputActions.updateSearchTerm(searchTerm);
      this.performTextSearch(searchTerm);
    });
  }

  /** Queue a search using its metadata so that it can be performed later */
  queueSearch(term, fields, limit, page, searchState?) {
    const state = this.state.set(
      'queuedSearch',
      Map({
        term: term,
        fields: fields,
        limit: limit,
        page: page,
        searchState: searchState,
      })
    );
    this.setState(state);
  }

  /**
   * When a search request is successfully made, the response might be returned directly (textSearch) or returned
   * along with args as well (fieldsSearch)
   * @param  {object} argsAndResponse response or args
   */
  onSearchSuccess({args, response}) {
    if (args && response) {
      this.updateSearchResultsAndState(args, response);
      if (this.state.queuedSearch.count() > 0) {
        this.performQueuedSearch(this.state.queuedSearch);
      }
    } else {
      console.error(`Unexpected error in onSearchSuccess: args = ${args}, response = ${response}`);
    }
    setTimeout(() => CrmSyncActions.crmSupportsCloud(), 500);
  }

  onSearchError({args, response}) {
    // server will return 404 if search index is missing for account
    const missingIndex = response.response.status === 404;
    this.setState(
      this.state.merge({
        searchLoading: false,
        searchError: true,
        missingIndex: missingIndex,
      })
    );
  }

  /**
   * Perform a queued search to update the search results to the latest
   * @param  {Map} queuedSearch A queued search object
   */
  performQueuedSearch(queuedSearch) {
    let {term, fields, limit, page} = queuedSearch.toObject();
    const storeState = this.state.set('queuedSearch', Map());
    this.setState(storeState);
    if (term && term.length > 0) {
      fields = this.fieldsAugmentedWithSearchType(fields, term);
    }
    this.fieldsSearchDirect(term, fields, limit, page);
  }

  /**
   * Return an object filtered from the search fields in a response body, whose keys are filtered depending
   * on the current state of the search page.
   * @param {object} reqBodyFields The current fields returned from the search request
   */
  requestBodySearchFieldsFilteredOut(reqBodyFields) {
    if (this.state.isSearchingLibraryTags) {
      reqBodyFields = this.fieldsWithoutLibrary(reqBodyFields);
    } else if (this.state.isSearchingSelf) {
      reqBodyFields = this.fieldsWithoutSelf(reqBodyFields);
    }

    if (this.state.fileId) {
      reqBodyFields = this.fieldsWithoutFileId(reqBodyFields);
    }

    return reqBodyFields;
  }

  updateSearchResultsAndState([term], response) {
    const storeState = this.state;
    if (term.length === 0) {
      // Since the user is clearing the search field and there might be some active searches available,
      // we trigger an action from SavedSearchActions in order to inform SavedSearchStore to merge
      // its `searches` with `activeSearches`
      SavedSearchActions.mergeActiveIntoSearches.defer();
    }
    const {request_body = {}, ...searchResultsProps} = response.data;
    const {fields = {}} = request_body;

    if (response.data.request_body) {
      SearchFieldsActions.updateSearchExpression.defer(fields);
    }

    const areSearchesQueued = this.state.queuedSearch.count() > 0;
    const fieldsHaveChanged = Map(fields).size > 0;

    const searchResults = new SearchResults(searchResultsProps);
    FileUploadActions.updateVotes.defer(searchResults.results);

    this.setState(
      storeState.merge({
        searchResults: searchResults,
        searchTotal: response.data.total,
        userIsSearchingSth: fieldsHaveChanged,
        searchFields: this.requestBodySearchFieldsFilteredOut(fields),
        searchLimit: request_body.limit,
        searchPage: request_body.page,
        searchLoading: !areSearchesQueued,
      })
    );

    if (!areSearchesQueued) {
      this.getInstance<typeof LibrarySource>()
        .getInfoAboutFiles({
          file_upload_ids: this.state.searchResults.results.map(res => res.id),
        })
        .catch(console.error);
    }
  }

  onGetInfoAboutFilesSuccess() {
    this.setState(
      this.state.merge({
        searchLoading: false,
        isInitialLoad: false,
      })
    );
  }

  /**
   * Return `true` if the current active search, including all the info regarding search term, fields,
   * limit, page, is a text search; `false` if the current active search involves
   * fields as well
   * @return {Boolean} `true` if the current active search is a text-only search; `false` otherwise
   */
  isActiveSearchTextOnly() {
    const fields = this.state.searchFields;
    return !fields || Map(fields).count() === 0;
  }

  /**
   * Perform a text-only search with a search term.
   * @param  {string} term   A string search term
   * @param  {number} limit  An integer limit
   * @param  {number} page   An integer page number
   */
  performTextSearch(term, limit = defaultLimit, page = defaultPage) {
    const fields = this.fieldsToSearch();
    if (Object.keys(fields).length > 0) {
      this.performFieldsSearch(term, fields, limit, page);
    } else {
      this.performOrQueueTextSearch(term, limit, page);
    }
  }

  /** Perform or queue a text-based search */
  performOrQueueTextSearch(term, limit, page) {
    if (this.state.searchLoading) {
      this.queueSearch(term, {}, limit, page, Map());
    } else {
      this.textSearchDirect(term, limit, page);
    }
  }

  /** Perform a search or queue it depending whether there is a search in progress at the moment or not */
  performOrQueueSearch(term, fields, limit, page) {
    if (this.state.searchType !== st.miAll) {
      term = ''; // Send term to server only if type is All. See EV-6491.
    }
    if (this.state.searchLoading) {
      this.queueSearch(term, fields, limit, page);
    } else {
      this.fieldsSearchDirect(term, fields, limit, page);
    }
  }

  /**
   * Return a {limit, page} object whose values are sanitized, i.e., whose values are meaningful
   * @param  {number} limit An integer limit which might be 0
   * @param  {number} page  An integer page which might be 0
   * @return {object}       A {limit, page} object
   */
  sanitizedLimitAndPage(limit, page) {
    if (limit === 0) {
      limit = defaultLimit;
    }
    if (page === 0) {
      page = defaultPage;
    }
    return {limit: limit, page: page};
  }

  /**
   * Callback triggered when the user switches from other tab to "Calls" or opens the "Calls" tab
   * for the first time. Two cases should happen:
   * 1) If "Calls" is opened for the first time, then we perform a text search with term == ''
   * 2) If "Calls" is switched from another tab, then we perform a fields search with term == ''
   */
  onMyCallsSearch() {
    const {term, fields, limit, page} = this.currentSearchParameters();
    const {limit: sanitizedLimit, page: sanitizedPage} = this.sanitizedLimitAndPage(limit, page);
    if (this.isActiveSearchTextOnly()) {
      this.performTextSearch(term, sanitizedLimit, sanitizedPage);
    } else {
      this.performFieldsSearch(term, fields, sanitizedLimit, sanitizedPage);
    }
  }

  onTextSearch(text) {
    const fields = this.fieldsAugmentedWithSearchType(this.fieldsToSearch(), text);
    if (Object.keys(fields).length > 0) {
      this.performOrQueueSearch(text, fields, defaultLimit, defaultPage);
    } else {
      this.performOrQueueTextSearch(text, defaultLimit, defaultPage);
    }
  }

  onDirectTextSearch(text) {
    const fields = this.fieldsAugmentedWithSearchType(this.fieldsToSearch(), text);
    if (Object.keys(fields).length > 0) {
      this.fieldsSearchDirect(text, fields, defaultLimit, defaultPage);
    } else {
      this.textSearchDirect(text, defaultLimit, defaultPage);
    }
  }

  /** Perform a direct text search as-is */
  textSearchDirect(text, limit, page) {
    this.setLoadingState();
    this.getInstance<typeof SearchSource>()
      .textSearch(text, limit, page, this.state.sortByDate)
      .catch(error => (error.status === 404 ? null : Promise.resolve(error)));
  }

  /** Perform a direct fields search as-is */
  fieldsSearchDirect(term, fields, limit, page) {
    this.setLoadingState();
    this.getInstance<typeof SearchSource>()
      .fieldsSearch(term, fields, limit, page, this.state.sortByDate)
      .catch(console.error);
  }

  setLoadingState() {
    this.setState(
      this.state.merge({
        searchLoading: true,
        searchError: false,
      })
    );
  }

  toggleSortByDate(sortByDate) {
    this.setState(this.state.set('sortByDate', sortByDate));
  }
}

const SearchStore = alt.createStore(SearchStore_unconnected, 'SearchStore');
export default SearchStore;

const createElementA = function (url) {
  const elementA = document.createElement('a');
  elementA.href = url;
  return elementA;
};

/**
 * Convert a fully qualified URL to a relative url so it can be used by browserHistory.push()
 * @param  {string} url A fully qualified URL or a relative URL
 * @return {string}     A relative URL
 */
const convertFullyQualifiedURLToRelativeURL = function (url) {
  if (url.indexOf('http') > -1) {
    return createElementA(url).pathname;
  }
  return url;
};

/**
 * Return a search `fields` object whose keys are augmented / updated according to a `searchType`
 * and a `searchTerm`
 * @param fields Search fields object
 * @param searchType Current search type
 * @param searchTerm Current search term
 */
function fieldsAugmentedWithSearchTypeAndTerm(fields, searchType, searchTerm) {
  const fieldType = fieldTypeFromSearchType(searchType);
  switch (searchType) {
    case st.miCaller:
      fields['Caller'] = {type: fieldType, values: [searchTerm]};
      break;
    case st.miContact:
      fields['Contact'] = {type: fieldType, values: [searchTerm]};
      break;
    case st.miCompany:
      fields['Company'] = {type: fieldType, values: [searchTerm]};
      break;
    case st.miCallStage:
      fields['Call Stage'] = {type: fieldType, values: [searchTerm]};
      break;
    case st.miCampaign:
      fields['Campaign'] = {type: fieldType, values: [searchTerm]};
      break;
    case st.miKeywords:
      fields['Keyword / Topic'] = {
        type: fieldType,
        conditions: [
          {
            value: searchTerm,
            isTopic: false,
            mentioned: true,
            by: 'anyone',
          },
        ],
      };
      break;
    case st.miPhone:
      // combined=true because the top-search-bar powered phone search
      // combines the results from multiple phone number fields
      fields['Phone'] = {
        type: fieldType,
        values: [searchTerm],
        combined: true,
      };
      break;
    case st.miAnnotations:
      fields['or'] = [
        {Comments: {type: 'all_of', values: [searchTerm]}},
        {Highlights: {type: 'all_of', values: [searchTerm]}},
      ];
      break;
    default:
      break;
  }
  return fields;
}

/** Return the value of `type` used in a /search request body's fields key given the search type. Notice that we need
 * to use SearchFieldsStore.searchFields in order to obtain the correct `type` value
 * @param searchType Current search type
 * */
function fieldTypeFromSearchType(searchType) {
  if (searchType === st.miKeywords) {
    return 'all_of';
  }
  const searchFieldName = searchFieldNameFromMenuItemPrimaryText(searchType);
  const state = SearchFieldsStore.getState();
  let fieldsDefinitions = state.get('fullSearchFields');
  if (!fieldsDefinitions) {
    fieldsDefinitions = state.get('searchFields');
  }
  let fieldType = fieldsDefinitions.find(searchField => {
    return searchField.name === searchFieldName;
  });
  if (fieldType) {
    fieldType = fieldType.type;
  }
  if (fieldType === 'infinite_string') {
    return 'contains';
  } else if (fieldType === 'finite_string') {
    return 'all_of';
  } else if (fieldType === 'phone') {
    return 'some_of';
  }
}

const dsvt = ConstValues.datetimeSearchValueTypes;
const VOTING_SEARCH_FIELD_NAME = 'Voting';

/**
 * Stores metadata about search fields which are obtained from the sub-field `fields`
 * in the JSON response of a search call
 */
@immutableStore
class SearchFieldsStore_unconnected extends Store<any> {
  constructor() {
    super();
    this.state = Map({
      // Unlike searchFields which changes according to the search requests, fullSearchFields stays the same and only
      // gets updated once.
      fullSearchFields: List<Field>(),
      hasFullSearchFields: false,
      // searchFields gets updated whenever the search request changes
      // TODO: this seems to be a List at several points in the code, maybe change?
      searchFields: Map(),
      // `searchExpression` is an expression document used for expression-based searches, e.g., complex searches
      searchExpression: Map(),
      // `searchFieldsState` is an object that stores metadata about the states of search fields, e.g., whether
      // a boolean search field has been toggled on or the range (min, max) specified for a numeric search field
      searchFieldsState: Map(),
      // should this be part of the above fields? who knows
      keywordTopicLineItems: List(),
      // all or any k/t line item?
      keywordTopicAllOrAny: ALL_OF_VALUE,
    });
    this.bindActions(CallActions);
    this.bindActions(CallerTeamComboboxSearchFieldActions);
    this.bindActions(SearchFieldsActions);
    this.bindActions(SearchTabActions);
    this.bindActions(ManagerDashboardActions);
    this.bindActions(FileUploadActions);
    this.bindActions(VotingRadioButtonsSearchFieldActions);
  }

  onSwitchWithMyCalls() {
    let state = this.state;
    let fieldsState = state.get('searchFieldsState');
    fieldsState = fieldsState.deleteIn([ConstValues.stringWidgetType, 'Caller']);
    fieldsState = fieldsState.delete('Caller');
    state = state.set('searchFieldsState', fieldsState);
    this.setState(state);
  }

  /**
   * Update the toggle state(s) within `this.state.get('searchFieldsState')` by setting the toggled state of the
   * `prevSearchType` to false
   * @param args {array} [prevSearchType, searchType] Previous search type and current search type
   */
  onUpdateSearchType(args) {
    const [prevSearchType] = args;
    this.removeSearchTypeRelatedData(List([prevSearchType]));
  }

  /**
   * Event handler for SearchFieldsActions.removeSearchType to remove all the data related to a search type
   * @param searchTypes {List} Search type(s) whose data to be removed
   */
  onRemoveSearchType(searchTypes) {
    this.removeSearchTypeRelatedData(searchTypes, true);
  }

  /**
   * Remove all the data related to a previous search type
   * @param prevSearchTypes {List} Previous search type(s)
   */
  removeSearchTypeRelatedData(prevSearchTypes, removeValues = false) {
    let state = this.state;
    prevSearchTypes.forEach(prevSearchType => {
      const searchFieldName = searchFieldNameFromMenuItemPrimaryText(prevSearchType);
      if (searchFieldName) {
        let toggleState = Map();
        toggleState = this.toggleStateWithFalseForSearchType(
          toggleState,
          prevSearchType,
          searchFieldName
        );
        let fieldsState = this.state.get('searchFieldsState');
        if (toggleState.count() > 0) {
          fieldsState = fieldsState.merge(toggleState);
        }
        if (removeValues) {
          fieldsState = fieldsState.delete(searchFieldName);
        }
        state = state.set('searchFieldsState', fieldsState);
        let searchExpression = this.state.get('searchExpression');
        searchExpression = searchExpression.delete(searchFieldName);
        state = state.set('searchExpression', searchExpression);
      }
    });
    let searchFieldsState = state.get('searchFieldsState');
    searchFieldsState.forEach((data, key) => {
      const searchFieldName = searchFieldNameFromMenuItemPrimaryText(key);
      if (searchFieldName) {
        searchFieldsState = searchFieldsState.setIn(
          [ConstValues.stringWidgetType, searchFieldName],
          true
        );
      }
    });
    state = state.set('searchFieldsState', searchFieldsState);
    setImmediate(() => {
      this.setState(state);
    });
  }

  /** Set the toggled state of a search field to false according to its search type */
  toggleStateWithFalseForSearchType(toggleState, searchType, searchFieldName) {
    switch (searchType) {
      case st.miAll:
        break;
      default:
        toggleState = toggleState.setIn([ConstValues.stringWidgetType, searchFieldName], false);
        break;
    }
    return toggleState;
  }

  onExecuteInactiveSearch(savedSearch) {
    const {state} = savedSearch;
    this.setState(this.state.set('searchFieldsState', state));
  }

  onSearchSuccess(data) {
    this.waitFor(SearchStore);
    const state = SearchStore.getState();
    this.updateSearchFields(
      {
        fields: state.searchResults.fields,
      },
      data.response.data.fields,
      !this.state.get('hasFullSearchFields')
    );
    if (state.prevSearchTypes.count() > 0) {
      setTimeout(() => {
        const currentSearchType = state.searchType;
        const searchTypesToRemove = state.prevSearchTypes.filterNot(
          searchType => searchType === currentSearchType
        );
        this.removeSearchTypeRelatedData(searchTypesToRemove, true);
        CallActions.clearPrevSearchTypes();
      }, 1000);
    }
  }

  findFilesByIdSuccess({fields}) {
    const hasFields = this.state.get('hasFullSearchFields');
    if (!hasFields) {
      this.setState(this.state.set('fullSearchFields', fields).set('hasFullSearchFields', true));
    }
  }

  /**
   * Use this.state.get('searchExpression') to refresh the search results
   */
  refreshSearch() {
    const searchExpression = this.state.get('searchExpression');
    CallActions.fieldsSearch.defer(searchExpression);
  }

  /**
   * Update the boolean search expression
   * @param  {boolean} toggled Whether the `booleanSearchExpression` is toggled or not. If not, then all the boolean
   *  fields related search fields should be removed from `this.state.get('searchExpression')
   */
  onUpdateBooleanSearchExpression(toggled) {
    if (!toggled) {
      const searchExpression = this.state
        .get('searchExpression')
        .filterNot(x => typeof x === 'boolean');
      let newState = this.state.set('searchExpression', searchExpression);
      const searchFieldsState = newState
        .get('searchFieldsState')
        .filterNot(state => state.type === 'boolean');
      newState = newState.set('searchFieldsState', searchFieldsState);
      this.setState(newState);
      this.refreshSearch();
    }
  }

  /**
   * Return a `Field` Record object some of whose fields' values are preserved from the `stateSearchField`
   * object and while others are assigned from `searchField`. Therefore, the return value of this function is a `Field` object
   * whose field values are combined from both `stateSearchField` and `searchField`
   * @param  {Record} stateSearchField The current `Field` object stored in `this.state`
   * @param  {Record} searchField      The new `Field` object some of whose values should be assigned into
   *                                             the return value of this function
   * @param  {List} fieldsToOverride A list of names of fields whose values should be overridden by `searchField`
   * @return {[type]}                  [description]
   */
  preservedSearchField(stateSearchField, searchField, fieldsToOverride) {
    const searchFieldObject = stateSearchField.toObject();
    fieldsToOverride.forEach(field => {
      searchFieldObject[field] = searchField.get(field);
    });
    const options = searchFieldObject.options
      .map(option => {
        return option.toObject();
      })
      .toArray();
    searchFieldObject.options = options;
    const field = new Field(searchFieldObject);
    return field;
  }

  /**
   * Return a `searchFields` which is a `List` list, each of which has some of its values preserved while
   * others overridden by the other object
   * @param  {Map} state            A `SearchFieldsStore.state` Map object
   * @param  {List} searchFields     A list of `Field` objects
   * @param  {string} type             Type of the search fields
   * @param  {List} fieldsToOverride A list of field names to override
   * @return {List}                  A list of `Field` objects
   */
  preservedSearchFields(state, searchFields, type, fieldsToOverride) {
    const stateSearchFields = state.get('searchFields').filter(searchField => {
      return searchField.type === type;
    });
    let dataSearchFields = searchFields.filter(searchField => {
      return searchField.type === type;
    });
    dataSearchFields = dataSearchFields.map(searchField => {
      const stateSearchField = stateSearchFields.find(stateSearchField => {
        return stateSearchField.name === searchField.name;
      });
      return this.preservedSearchField(stateSearchField, searchField, fieldsToOverride);
    });
    dataSearchFields.forEach(searchField => {
      const index = searchFields.findIndex(dataSearchField => {
        return dataSearchField.name === searchField.name;
      });
      searchFields = searchFields.set(index, searchField);
    });
    return searchFields;
  }

  /**
   * Notice that we don't want to update the `min` and `max` values of a numeric
   * search field unless this is a keywords / text only search request
   * @param  {array} data Array of search fields
   * @param  {array} remoteSearchFields Array of `Field` objects in a search request's response
   * @param  {boolean} isDefaultSearch If true, then this search is a default search (* / empty)
   */
  updateSearchFields(data, remoteSearchFields, isDefaultSearch = false) {
    let localSearchFields = data.fields;
    if (
      remoteSearchFields &&
      Iterable.isIterable(remoteSearchFields) &&
      remoteSearchFields.count() > 0
    ) {
      // This is a fields-based search, so we skip updating the `min`, `max` values of all the numeric
      // search fields in `this.state.get('searchFields')` and keep them as-is
      localSearchFields = this.preservedSearchFields(
        this.state,
        remoteSearchFields,
        'numeric',
        List([
          'multiselect',
          'name',
          'options',
          'suggestions',
          'order',
          'type',
          'unit',
          'unit_fix_position',
        ])
      );
      localSearchFields = this.preservedSearchFields(
        this.state,
        remoteSearchFields,
        'finite_string',
        List(['min', 'max', 'multiselect', 'name', 'order', 'type', 'unit', 'unit_fix_position'])
      );
      localSearchFields = this.preservedSearchFields(
        this.state,
        remoteSearchFields,
        'enum',
        List(['min', 'max', 'name', 'order', 'type', 'unit', 'unit_fix_position'])
      );
    }
    localSearchFields = localSearchFields.sort((stateA, stateB) => {
      if (stateA.order < stateB.order) {
        return -1;
      } else if (stateA.order > stateB.order) {
        return 1;
      } else {
        // Keep this here for if-else-statement clarity. Should never happen
        return 0;
      }
    });
    let newState = this.state.set('searchFields', localSearchFields);
    if (isDefaultSearch) {
      newState = newState
        .set('fullSearchFields', localSearchFields)
        .set('hasFullSearchFields', true);
    }

    this.setState(newState);
  }

  /**
   * Update this.state.searchExpression using `request_body.fields`
   * @param  {object} fields The fields in a search request body
   */
  onUpdateSearchExpression(fields) {
    if (fields) {
      let searchExpression = this.state.get('searchExpression');
      searchExpression = searchExpression.merge(Map(fields));
      const state = this.state.set('searchExpression', searchExpression);
      this.setState(state);
      this.preventDefault();
    }
  }

  /**
   * Update `this.state.get('searchExpression')` using `args`
   * @param  {Array} args An array of arguments, i.e., [records/Field, checked]
   */
  onUpdateBooleanSearchField(args) {
    // `field` is a `records/Field` object
    const field = args[0];
    // `checked` is a boolean which specifies whether `args[0]` is checked or not
    const checked = args[1];
    let newState = this.state;
    if (checked) {
      newState = this.state.setIn(['searchExpression', field.name], checked);
    } else {
      // Instead of marking a boolean search field as `false` in this.state.get('searchExpression'),
      // we remove it from this.state.get('searchExpression')
      newState = this.state.removeIn(['searchExpression', field.name]);
    }
    this.setState(newState);
    this.refreshSearch();
  }

  /**
   * Update `this.state.get('searchExpression') using `args`
   * @param  {Array} args An array of arguments, i.e., [records/Field, values]
   */
  onUpdateNumericSearchField(args) {
    const field = args[0];
    const values = args[1];
    const numericSearchDoc = {
      gte: values.min,
      lte: values.max,
    };
    const newState = this.state.setIn(['searchExpression', field.name], numericSearchDoc);
    this.setState(newState);
    setTimeout(() => {
      this.refreshSearch();
    }, 1000);
  }

  /**
   * Update `this.state.get('searchExpression')` and `this.state.get('searchFieldsState')
   * using `args`
   * @param  {Array} args An array of arguments, i.e., [records/Field, toggled]
   */
  onUpdateNumericSearchExpression(args) {
    const field = args[0];
    const toggled = args[1];
    if (!toggled) {
      const searchExpression = this.state
        .get('searchExpression')
        .filterNot((expr, key) => key === field.name);
      let newState = this.state.set('searchExpression', searchExpression);
      const searchFieldsState = newState
        .get('searchFieldsState')
        .filterNot(state => state.name === field.name);
      newState = newState.set('searchFieldsState', searchFieldsState);
      this.setState(newState);
      this.refreshSearch();
    }
  }

  /**
   * Update the toggled state of a search widget
   * @param  {Array} args Arugments of this action
   */
  onUpdateSearchWidgetToggleState(args) {
    const [widgetType, toggled, searchField] = args;
    let state = this.state;
    if (searchField) {
      state = state.setIn(['searchFieldsState', widgetType, searchField.name], toggled);
    } else {
      state = state.setIn(['searchFieldsState', widgetType], toggled);
    }
    if (!toggled) {
      // Since `searchField` is un-toggled, we should also clear the search term as well as the
      // menu item status according to the current search type
      const searchType = SearchStore.getState().searchType;
      const searchFieldName = searchFieldNameFromMenuItemPrimaryText(searchType);
      if (searchField.name === searchFieldName) {
        setImmediate(() => {
          SearchInputActions.updateSearchTerm('');
          SearchFieldsActions.clearMenuItemStatus(searchType);
          // Refresh the search type so that the search fields are cleared
          CallActions.resetSearchType();
        });
      }
    }
    this.setState(state);
  }

  /**
   * Update the state of the search fields as a whole
   * @param  {object} state The new state
   */
  onUpdateSearchFieldsStateInWhole(state) {
    state = state.map((val, key) => {
      if (typeof val === 'object') {
        return Map(val);
      } else if (Array.isArray(val)) {
        return List(val);
      }
      return val;
    });
    const newState = this.state.set('searchFieldsState', state);
    this.setState(newState);
  }

  /**
   * Update the state of the search fields
   * @param  {object} args An object that maps `state` to a dictionary for a search field
   */
  onUpdateSearchFieldsState(args) {
    const argsState = args.state;
    let newState = this.state;
    if (argsState.get('type') === 'boolean') {
      if (argsState.get('checked')) {
        newState = this.state.setIn(['searchFieldsState', argsState.get('name')], argsState);
      } else {
        newState = this.state.removeIn(['searchFieldsState', argsState.get('name')]);
      }
    } else {
      newState = this.state.setIn(['searchFieldsState', argsState.get('name')], argsState);
    }
    this.setState(newState);
  }

  /**
   * Update `this.state.get('searchExpression')` using `args`
   * @param  {Array} args An array of arguments, i.e.,
   * [records/Field, Date, ConstValues.datetimeSearchValueTypes]
   */
  onUpdateDatetimeSearchField(args) {
    const field = args[0];
    const datetime = args[1];
    const type = args[2];
    if (type === dsvt.to || type === dsvt.from) {
      let shouldRefreshSearchResults = false;
      let fieldSearchExpression = this.state.getIn(['searchExpression', field.name]);
      if (type === dsvt.to && fieldSearchExpression && fieldSearchExpression.gte) {
        fieldSearchExpression.lte = datetime.toISOString();
        shouldRefreshSearchResults = true;
      } else if (type === dsvt.from && fieldSearchExpression && fieldSearchExpression.lte) {
        fieldSearchExpression.gte = datetime.toISOString();
        shouldRefreshSearchResults = true;
      } else {
        if (type === dsvt.to) {
          fieldSearchExpression = {
            lte: datetime.toISOString(),
          };
        } else if (type === dsvt.from) {
          fieldSearchExpression = {
            gte: datetime.toISOString(),
          };
        }
      }
      let newState = this.state.setIn(['searchExpression', field.name], fieldSearchExpression);
      // Also update this.state.get('searchFields') so that <DateSearchField /> can update its display without
      // triggering a server-side search request
      let searchFieldsState = newState.get('searchFields');
      let datetimeSearchFieldIndex = null;
      let fieldState = searchFieldsState.find((data, index) => {
        if (data.name === field.name) {
          datetimeSearchFieldIndex = index;
          return true;
        }
        return false;
      });
      fieldState = fieldState.set('value', fieldSearchExpression);
      searchFieldsState = searchFieldsState.set(datetimeSearchFieldIndex, fieldState);
      newState = newState.set('searchFields', searchFieldsState);
      this.setState(newState);
      if (shouldRefreshSearchResults) {
        this.refreshSearch();
      }
    } else if (type === dsvt.exact) {
      const dayStart = new Date(datetime.getTime());
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(datetime.getTime());
      dayEnd.setHours(23, 59, 59, 999);
      const fieldSearchExpression = {
        gt: dayStart.toISOString(),
        lt: dayEnd.toISOString(),
      };
      const newState = this.state.setIn(['searchExpression', field.name], fieldSearchExpression);
      this.setState(newState);
      this.refreshSearch();
    } else if (type === dsvt.before) {
      const fieldSearchExpression = {
        lte: datetime.toISOString(),
      };
      const newState = this.state.setIn(['searchExpression', field.name], fieldSearchExpression);
      this.setState(newState);
      this.refreshSearch();
    } else if (type === dsvt.within) {
      const fieldSearchExpression = {
        gte: datetime.toISOString(),
      };
      const newState = this.state.setIn(['searchExpression', field.name], fieldSearchExpression);
      this.setState(newState);
      this.refreshSearch();
    }
  }

  /**
   * Remove all searchExpression and searchFieldsState so that the UI rendering goes back to
   * the vanilla state / condition
   */
  onClearAllSearchFieldStates() {
    this.waitFor(SearchStore);
    this.setState(
      Map({
        searchFields: Map(),
        searchExpression: Map(),
        searchFieldsState: Map(),
      })
    );
  }

  /**
   * Remove all searchExpression and searchFieldsState so that the UI rendering goes back to the vanilla state
   * / condition on the "My Calls" page or the "Library" page
   * @param fields {object} Vanilla search fields for the current page which is either "My Calls" or "Library"
   */
  onClearMyOrLibraryTagSearchFieldStates(fields) {
    this.waitFor(SearchStore);
    if (!Iterable.isIterable(fields)) {
      fields = Map(fields);
    }
    this.setState(
      Map({
        searchFields: fields,
        searchExpression: Map(),
        searchFieldsState: Map(),
      })
    );
    this.refreshSearch();
  }

  onRemoveSearchField(field) {
    const searchExpression = this.state
      .get('searchExpression')
      .filterNot((expr, key) => key === field.name);
    let newState = this.state.set('searchExpression', searchExpression);
    const searchFieldsState = newState
      .get('searchFieldsState')
      .filterNot((state, key) => key === field.name);
    newState = newState.set('searchFieldsState', searchFieldsState);
    this.setState(newState);
    this.refreshSearch();
  }

  /**
   * Update the datetime search expression
   * @param {Array} args An array with [records/Field, toggled]
   */
  onUpdateDatetimeSearchExpression(args) {
    const field = args[0];
    const toggled = args[1];
    if (!toggled) {
      const searchExpression = this.state
        .get('searchExpression')
        .filterNot((expr, key) => key === field.name);
      let newState = this.state.set('searchExpression', searchExpression);
      const searchFieldsState = newState
        .get('searchFieldsState')
        .filterNot(state => state.name === field.name);
      newState = newState.set('searchFieldsState', searchFieldsState);
      this.setState(newState);
      this.refreshSearch();
    }
  }

  /**
   * Update `this.state.get('searchExpression')` using `args`
   * @param  {Array} args [records/Field, value]
   */
  onUpdateEnumSearchField(args) {
    const field = args[0];
    const optValue = args[1];
    const newState = this.state.setIn(['searchExpression', field.name], optValue);
    this.setState(newState);
    this.refreshSearch();
  }

  /**
   * Update all the enum search fields in `this.state.get('searchExpression')` because it's caused by a user
   * toggle / un-toggle the whole enum search section in the left sidebar
   * @param {Array} args [records/Field, toggled]
   */
  onUpdateEnumSearchExpression(args) {
    const field = args[0];
    const toggled = args[1];
    if (!toggled) {
      const searchExpression = this.state
        .get('searchExpression')
        .filterNot((expr, key) => key === field.name);
      let newState = this.state.set('searchExpression', searchExpression);
      const searchFieldsState = newState
        .get('searchFieldsState')
        .filterNot(state => state.name === field.name);
      newState = newState.set('searchFieldsState', searchFieldsState);
      this.setState(newState);
      this.refreshSearch();
    }
  }

  /**
   * Update a search field expression with raw data no checking (for fields with a  nested structure)
   * @param {Array} args [records/Field, value]
   */
  onUpdateKeywordTopic(args) {
    const field = args[0];
    const optValue = args[1];
    const newValue = Map({
      name: field.name,
      type: field.type,
      selectedOptionKey: optValue.type,
      conditions: optValue.conditions,
    });
    const newState = this.state
      .setIn(['searchExpression', field.name], optValue)
      .setIn(['searchFieldsState', KEYWORD_TOPIC], newValue);

    this.setState(newState);

    if (optValue && optValue.conditions && optValue.conditions.size > 0) {
      this.refreshSearch();
    }
  }

  /**
   * Update `this.state.get('searchExpression')` and `this.state.get('searchFieldsState')` using
   * `args` so that the search results are updated if necessary
   * @param  {Array} args An array of arguments, i.e., [records/Field, key]
   */
  onUpdateStringSearchFieldSelectedOptionKey(args) {
    const field = args[0];
    const key = args[1];
    // Update `this.state.get('searchFieldsState')`` using `args`
    let searchFieldState = this.state.getIn(['searchFieldsState', field.name]);
    searchFieldState = searchFieldState.set('selectedOptionKey', key);
    let newState = this.state.setIn(['searchFieldsState', field.name], searchFieldState);
    // Update `this.state.get('searchExpression')` using `args`
    let shouldRefreshSearchResults = false;
    let fieldSearchExpression = this.state.getIn(['searchExpression', field.name]);
    if (fieldSearchExpression) {
      fieldSearchExpression.type = key;
      if (fieldSearchExpression.values.length > 0) {
        shouldRefreshSearchResults = true;
      }
    } else {
      fieldSearchExpression = {
        type: key,
        values: [],
      };
    }
    newState = newState.setIn(['searchExpression', field.name], fieldSearchExpression);
    this.setState(newState);
    if (shouldRefreshSearchResults) {
      this.refreshSearch();
    }
  }

  /**
   * Initialize or remove a string search field's `searchFieldsState` and `searchExpression`
   * according to `args`
   * @param  {Array} args An array of arguments, i.e., [records/Field, toggled]
   */
  onUpdateStringSearchExpression(args) {
    const field = args[0];
    const toggled = args[1];
    let fieldState = this.state.getIn(['searchFieldsState', field.name]);
    let fieldSearchExpression = this.state.getIn(['searchExpression', field.name]);
    let newState;
    if (toggled) {
      // Initialize `searchFieldsState` and `searchExpression` for `field` if one does not already exist
      if (!fieldState) {
        // initialize state
        let selectedOptionKey;
        if (field.type === 'finite_string') {
          selectedOptionKey = ConstValues.finiteStringSearchFieldDefaultSelectedOptionKey;
        } else if (field.type === 'infinite_string') {
          selectedOptionKey = ConstValues.infiniteStringSearchFieldDefaultSelectedOptionKey;
        }
        fieldState = Map({
          name: field.name,
          type: field.type,
          selectedOptionKey: selectedOptionKey,
        });
        newState = this.state.setIn(['searchFieldsState', field.name], fieldState);
        this.setState(newState);
      }
      if (!fieldSearchExpression) {
        // initialize searchExpression
        let type;
        if (field.type === 'finite_string') {
          type = ConstValues.finiteStringSearchFieldDefaultSelectedOptionKey;
        } else if (field.type === 'infinite_string') {
          type = ConstValues.infiniteStringSearchFieldDefaultSelectedOptionKey;
        }
        fieldSearchExpression = {
          type: type,
          values: [],
        };
        newState = this.state.setIn(['searchExpression', field.name], fieldSearchExpression);
        this.setState(newState);
      }
    } else {
      if (fieldState) {
        // clear `fieldState.values` so that they are not rendered in the UI
        if (fieldState.values) {
          fieldState.values = [];
          newState = this.state.setIn(['searchFieldsState', field.name], fieldState);
          this.setState(newState);
        }
      }
      if (fieldSearchExpression) {
        const searchExpression = this.state
          .get('searchExpression')
          .filterNot((expr, key) => key === field.name);
        newState = this.state.set('searchExpression', searchExpression);
        this.setState(newState);
        this.refreshSearch();
      }
    }
  }

  /**
   * Update `this.state.get('searchExpression')` and `this.state.get('searchFieldsState')`
   * using `args` so that the search results are updated if necessary for a phone number
   * @param args {Array} array of action arguments
   */
  onUpdatePhoneSearchField(args) {
    const field = args[0];
    const value = args[1];
    const newSearchFieldsState = this._searchFieldStateWithFieldValue(
      field.name,
      value,
      null,
      true
    );
    if (newSearchFieldsState) {
      this.setState(newSearchFieldsState);
    }
    const newSearchExpressionState = this._searchExpressionWithFieldValue(
      field.name,
      value,
      null,
      'replace'
    );
    if (newSearchExpressionState) {
      this.setState(newSearchExpressionState);
      this.refreshSearch();
    }
  }

  /**
   * Update `this.state.get('searchExpression')` and `this.state.get('searchFieldsState')` using `args`
   * so that the search results are updated if necessary and the search widgets rendering options
   * are updated if necessary
   * @param  {Array} args An array of action arguments, i.e., [records/Field, value]
   */
  onUpdateStringSearchField(args) {
    const field = args[0];
    const value = args[1];
    // Update this.state.get('searchFieldsState') using `field` and `value`
    const newSearchFieldsState = this._searchFieldStateWithFieldValue(field.name, value);
    if (newSearchFieldsState) {
      this.setState(newSearchFieldsState);
    }
    // Update this.state.get('searchExpression') using `field` and `value`
    const newSearchExpressionState = this._searchExpressionWithFieldValue(field.name, value);
    if (newSearchExpressionState) {
      this.setState(newSearchExpressionState);
      this.refreshSearch();
    }
  }

  onRemoveCaller(args) {
    this.waitFor(CallerTeamComboboxSearchFieldStore);
    const {caller, selectedOptionKey} = args;
    this._updateCallerSearchField(caller, selectedOptionKey, 'remove');
  }

  onChangeCaller(args) {
    this.waitFor(CallerTeamComboboxSearchFieldStore);
    const {selectedOptionKey} = args;
    // @ts-ignore this store is untyped, not sure why it errors here
    const selectedOptions = CallerTeamComboboxSearchFieldStore.selectedOptions(
      CallerTeamComboboxSearchFieldStore.getState(),
      selectedOptionKey
    );
    const values = selectedOptions.map(opt => opt.id).toArray();
    this._updateCallerSearchFieldWithValues(selectedOptionKey, values);
  }

  onResetCaller() {
    this.waitFor(CallerTeamComboboxSearchFieldStore);
    this._removeCallerSearchField();
  }

  onResetVoting() {
    this.waitFor(VotingRadioButtonsSearchFieldStore);
    this._removeVotingSearchField();
  }

  /**
   * restore a previously saved search state
   * @param newState
   */
  onResetSearch(newState) {
    this.setState(newState.searchFieldsStoreState);
  }

  /**
   * Update the current search expression to include a voting-related search
   * @param {VotingSearchValue} voting Current voting value/choice
   */
  onChangeVoting(voting) {
    this.waitFor(VotingRadioButtonsSearchFieldStore);
    // @ts-ignore this store is untyped, not sure why it errors here
    const votingValue = VotingRadioButtonsSearchFieldStore.getVotingValue(
      VotingRadioButtonsSearchFieldStore.getState()
    );
    this._updateVotingSearchFieldWithValue(votingValue);
  }

  _removeVotingSearchField() {
    let updatedState = this.state.deleteIn(['searchFieldsState', VOTING_SEARCH_FIELD_NAME]);
    updatedState = updatedState.deleteIn([
      'searchFieldsState',
      ConstValues.radioButtonsWidgetTypeForVoting,
    ]);
    updatedState = updatedState.deleteIn(['searchExpression', VOTING_SEARCH_FIELD_NAME]);
    this.setState(updatedState);
    this.refreshSearch();
  }

  _removeCallerSearchField() {
    const currentCallerField = this.state.getIn(['searchFieldsState', 'Caller']);
    if (currentCallerField) {
      let updatedState = this.state.deleteIn(['searchFieldsState', 'Caller']);
      updatedState = updatedState.deleteIn(['searchFieldsState', ConstValues.comboboxWidgetType]);
      updatedState = updatedState.deleteIn(['searchExpression', 'Caller']);
      this.setState(updatedState);
      this.refreshSearch();
    }
  }

  _updateCallerSearchFieldWithValues(selectedOptionKey, values) {
    const callerFieldName = 'Caller';
    let searchField = this.state.getIn(['searchFieldsState', callerFieldName]);
    if (!searchField) {
      searchField = Map();
    }
    let target;
    if (selectedOptionKey === 'team_search') {
      target = 'team';
    } else if (selectedOptionKey === 'rep_search') {
      target = 'rep';
    } else if (selectedOptionKey === 'manager_search') {
      target = 'manager';
    }
    searchField = searchField.set('values', values);
    searchField = searchField.set('target', target);
    let state = this.state.setIn(['searchFieldsState', callerFieldName], searchField);

    let callerSearchExpression = state.getIn(['searchExpression', callerFieldName]);
    if (!callerSearchExpression) {
      callerSearchExpression = {};
    }
    callerSearchExpression.type = 'some_of';
    callerSearchExpression.values = values;
    callerSearchExpression.target = target;
    state = state.setIn(['searchExpression', callerFieldName], callerSearchExpression);

    this.setState(state);
    this.refreshSearch();
  }

  /**
   * Update the `values` related to voting searches
   * @param {VotingSearchValue} votingValue Current voting search value to update
   * @private
   */
  _updateVotingSearchFieldWithValue(votingValue) {
    let votingSearchField = this.state.getIn(['searchFieldsState', VOTING_SEARCH_FIELD_NAME]);
    if (!votingSearchField) {
      votingSearchField = Map();
    }
    votingSearchField = votingSearchField.set('values', votingValue.values);
    votingSearchField = votingSearchField.set('type', votingValue.type);
    let state = this.state.setIn(
      ['searchFieldsState', VOTING_SEARCH_FIELD_NAME],
      votingSearchField
    );
    state = state.setIn(['searchExpression', VOTING_SEARCH_FIELD_NAME], votingValue.toObject());
    this.setState(state);
    this.refreshSearch();
  }

  _updateCallerSearchField(caller, selectedOptionKey, updateType = 'add') {
    if (selectedOptionKey === 'team_search') {
      this._updateCallerSearchFieldWithSelected(caller.id, 'team', updateType);
    } else if (selectedOptionKey === 'rep_search') {
      this._updateCallerSearchFieldWithSelected(caller.id, 'rep', updateType);
    } else if (selectedOptionKey === 'manager_search') {
      this._updateCallerSearchFieldWithSelected(caller.id, 'manager', updateType);
    }
  }

  _updateCallerSearchFieldWithSelected(caller, target, updateType) {
    const newSearchExpressionState = this._searchExpressionWithFieldValue(
      'Caller',
      caller,
      target,
      updateType
    );
    if (newSearchExpressionState) {
      this.setState(newSearchExpressionState);
      this.refreshSearch();
    }
  }

  _searchFieldStateWithFieldValue(fieldName, value, target = null, replaceExisting = false) {
    let searchField = this.state.getIn(['searchFieldsState', fieldName]);
    if (searchField.get('values')) {
      let lst = List(searchField.get('values'));
      if (replaceExisting) {
        searchField = searchField.set('values', List([value]));
      } else {
        if (!lst.includes(value)) {
          lst = lst.push(value);
          searchField = searchField.set('values', lst);
        }
      }
    } else {
      searchField = searchField.set('values', List([value]));
    }
    if (target) {
      searchField = searchField.set('target', target);
    }
    const searchFieldsStoreState = this.state.setIn(['searchFieldsState', fieldName], searchField);
    return searchFieldsStoreState;
  }

  _searchExpressionWithFieldValue(fieldName, value, target = null, updateType = 'add') {
    let fieldSearchExpression = this.state.getIn(['searchExpression', fieldName]);
    if (!fieldSearchExpression) {
      fieldSearchExpression = {};
    }
    if (updateType === 'add') {
      fieldSearchExpression = this._searchExpressionAddedWithValue(fieldSearchExpression, value);
    } else if (updateType === 'remove') {
      fieldSearchExpression = this._searchExpressionRemovedWithValue(fieldSearchExpression, value);
    } else if (updateType === 'replace') {
      fieldSearchExpression = this._searchExpressionReplacedWithValue(fieldSearchExpression, value);
    }
    if (target) {
      fieldSearchExpression.target = target;
    }
    const searchExpression = this.state.setIn(
      ['searchExpression', fieldName],
      fieldSearchExpression
    );
    return searchExpression;
  }

  _searchExpressionReplacedWithValue(fieldSearchExpression, value) {
    fieldSearchExpression.values = List([value]);
    fieldSearchExpression.type = 'all_of';
    return fieldSearchExpression;
  }

  _searchExpressionAddedWithValue(fieldSearchExpression, value) {
    if (fieldSearchExpression.values) {
      let lst = List(fieldSearchExpression.values);
      if (!lst.includes(value)) {
        lst = lst.push(value);
        fieldSearchExpression.values = lst;
      }
    } else {
      fieldSearchExpression.values = List([value]);
      fieldSearchExpression.type = 'some_of';
    }
    return fieldSearchExpression;
  }

  _searchExpressionRemovedWithValue(fieldSearchExpression, value) {
    if (fieldSearchExpression.values) {
      let lst = List(fieldSearchExpression.values);
      lst = lst.filterNot(val => val === value);
      fieldSearchExpression.values = lst.toArray();
    }
    return fieldSearchExpression;
  }

  /**
   * Remove `value` from `this.state.get('searchExpression')` and `this.state.get('searchFieldsState')`
   * @param  {Array} args An array of arguments, i.e., [records/Field, value]
   */
  onRemoveStringSearchFieldSelectedValue(args) {
    const field = args[0];
    const origValue = args[1];
    const cleanValue = replaceDashWithSpaceAndRemoveWhitespaces(args[1]);
    // Remove `value` from this.state.get('searchFieldsState')
    let searchFieldState = this.state.getIn(['searchFieldsState', field.name]);
    let lst = List(searchFieldState.get('values'));
    lst = lst.filterNot(x => x === cleanValue || x === origValue);
    searchFieldState = searchFieldState.set('values', lst.toArray());
    let newState = this.state.setIn(['searchFieldsState', field.name], searchFieldState);
    this.setState(newState);
    // Remove `value` from this.state.get('searchExpression')
    const searchExpression = this.state.getIn(['searchExpression', field.name]);
    lst = searchExpression.values;
    if (!Iterable.isIterable(lst)) {
      lst = List(lst);
    }
    lst = lst.filterNot(x => x === cleanValue || x === origValue);
    searchExpression.values = lst;
    newState = this.state.setIn(['searchExpression', field.name], searchExpression);
    this.setState(newState);
    this.refreshSearch();
  }
}

export const SearchFieldsStore = alt.createStore(
  SearchFieldsStore_unconnected,
  'SearchFieldsStore'
);

/**
 * Return a string from val with its dashes replaced by spaces and excessive whitespaces stripped
 * @param val {string} String value to be transformed
 */
function replaceDashWithSpaceAndRemoveWhitespaces(val) {
  val = val.replace(/-/g, ' ');
  val = val.replace(/[^A-Za-z0-9\s]/g, '').replace(/\s{2,}/g, ' ');
  return val.trim();
}

export const getKeywordTopicLineItems = s => {
  // s = SearchFieldStore state.
  const conditions = s.get('searchFieldsState')?.get(KEYWORD_TOPIC)?.get('conditions');
  return conditions ? List(conditions) : List();
};
